import { supabaseClient, useUser } from '@auth-v2';
import { useQueries, useQuery } from '@tanstack/react-query';

import { fetchSwingAnalysis, SwingAnalysis } from '@data';
import { activeUserIdAtom, useEnsureAtomValue } from '@store';

import { queryKeys } from '../queryKeys';

type SwingAnalysisByUuid = Record<string, SwingAnalysis>;

export const useSwingAnalysis = (uuid = '') => {
    const { data: user } = useUser();
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useQuery({
        queryKey: queryKeys.swings.analysis(activeUserId, uuid),
        queryFn: () => fetchSwingAnalysis(supabaseClient, uuid),
        enabled: !!uuid && !!user?.id,
        retry: true,
        // Once the query is successful we no longer need to query it.
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    });
};

export const useSwingAnalyses = (uuids: string[]) => {
    const { data: user } = useUser();
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);

    const queries = useQueries({
        queries: uuids.map((uuid) => ({
            queryKey: queryKeys.swings.analysis(activeUserId, uuid),
            queryFn: () => fetchSwingAnalysis(supabaseClient, uuid),
            enabled: !!uuid && !!user?.id,
            retry: true,
            staleTime: Infinity,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        })),
    });

    const isLoading = queries.some((q) => q.isLoading);
    const isError = queries.some((q) => q.isError);

    const data = queries.reduce<SwingAnalysisByUuid>((acc, q, i) => {
        if (q.data) {
            acc[uuids[i]] = q.data;
        }
        return acc;
    }, {});

    return {
        data,
        isLoading,
        isError,
    };
};
